/**
 * PolicyEditor's alignment and callout extensions.
 *
 * Both deliberately avoid TipTap's own stock `@tiptap/extension-text-align`: its default
 * (and only) output is `style="text-align: …"`, and this app's backend sanitiser never lets
 * a `style` attribute survive on anything, by design (an inline style is an open-ended CSS
 * injection surface - background-image/content exfiltration, clickjacking overlays - in a way
 * a small fixed class enum isn't). So alignment is its own tiny extension that renders a
 * `class="np-align-*"` instead, following the exact pattern IndentParagraph (PolicyEditor.tsx)
 * already established for `class="ql-indent-N"`. TipTap's mergeAttributes concatenates class
 * values from multiple sources rather than overwriting, so a paragraph can carry both an
 * indent class and an alignment class at once.
 *
 * Callout blocks (Note/Caution/Warning) reuse the already-allowed <blockquote> tag with a
 * `class="np-callout-*"` rather than introducing a new tag - one smaller sanitiser surface,
 * and it matches the fixed three-type taxonomy real style guides (Google's developer style
 * guide, specifically) recommend instead of ad hoc labels.
 */
import { Extension, Node, mergeAttributes } from '@tiptap/core';

const ALIGN_VALUES = ['left', 'center', 'right', 'justify'] as const;
export type AlignValue = (typeof ALIGN_VALUES)[number];

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    textAlign: {
      setTextAlign: (alignment: AlignValue) => ReturnType;
      unsetTextAlign: () => ReturnType;
    };
  }
  interface Commands<ReturnType> {
    callout: {
      setCallout: (calloutType: CalloutType) => ReturnType;
      unsetCallout: () => ReturnType;
    };
  }
}

export const AlignAttribute = Extension.create({
  name: 'alignAttribute',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading'],
        attributes: {
          align: {
            default: null,
            parseHTML: (element: HTMLElement) => {
              const match = Array.from(element.classList).find((c) => c.startsWith('np-align-'));
              return match ? match.slice('np-align-'.length) : null;
            },
            renderHTML: (attributes: { align?: string | null }) =>
              attributes.align && (ALIGN_VALUES as readonly string[]).includes(attributes.align)
                ? { class: `np-align-${attributes.align}` }
                : {},
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setTextAlign:
        (alignment: AlignValue) =>
        ({ commands }) =>
          ['paragraph', 'heading'].map((type) => commands.updateAttributes(type, { align: alignment })).some(Boolean),
      unsetTextAlign:
        () =>
        ({ commands }) =>
          ['paragraph', 'heading'].map((type) => commands.resetAttributes(type, 'align')).some(Boolean),
    };
  },
});

export type CalloutType = 'note' | 'caution' | 'warning';
const CALLOUT_TYPES: CalloutType[] = ['note', 'caution', 'warning'];

export const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      calloutType: {
        default: 'note',
        parseHTML: (element: HTMLElement) => {
          const match = Array.from(element.classList).find((c) => c.startsWith('np-callout-'));
          const value = match ? match.slice('np-callout-'.length) : 'note';
          return CALLOUT_TYPES.includes(value as CalloutType) ? value : 'note';
        },
        renderHTML: (attributes: { calloutType?: string }) => ({
          class: `np-callout-${CALLOUT_TYPES.includes(attributes.calloutType as CalloutType) ? attributes.calloutType : 'note'}`,
        }),
      },
    };
  },

  parseHTML() {
    // Higher than the default rule priority (50, see prosemirror-model's ParseRule) so this is
    // tried before StarterKit's plain Blockquote node's own unconditional `blockquote` rule -
    // without that, a callout's own class-scoped selector would still lose to the generic one
    // and every saved callout would silently reopen as an ordinary quote block.
    return [{
      tag: 'blockquote.np-callout-note, blockquote.np-callout-caution, blockquote.np-callout-warning',
      priority: 100,
    }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['blockquote', mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setCallout:
        (calloutType: CalloutType) =>
        ({ commands }) =>
          commands.wrapIn(this.name, { calloutType }),
      unsetCallout:
        () =>
        ({ commands }) =>
          commands.lift(this.name),
    };
  },
});
